// Minimal QR code matrix generator (byte mode, version auto-selected up to V10,
// error correction level L). Enough for a URL like https://singlings.com/app.
// Based on ISO/IEC 18004. Produces a 2D boolean matrix (true = dark).
//
// This is intentionally self-contained — no external deps, tiny bundle size.

type Matrix = boolean[][];

// ─── Reed-Solomon over GF(256) ───────────────────────────────────────────────

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255]!;
}

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[(GF_LOG[a]! + GF_LOG[b]!) % 255]!;
}

function rsGenPoly(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i++) {
    const next: number[] = [];
    for (let j = 0; j <= poly.length; j++) {
      const a = j > 0 ? poly[j - 1]! : 0;
      const b = j < poly.length ? gfMul(poly[j]!, GF_EXP[i]!) : 0;
      next.push(a ^ b);
    }
    poly = next;
  }
  return poly;
}

function rsEncode(data: number[], eccLen: number): number[] {
  const gen = rsGenPoly(eccLen);
  const res = new Array(eccLen).fill(0) as number[];
  for (const b of data) {
    const factor = b ^ res[0]!;
    res.shift();
    res.push(0);
    if (factor !== 0) {
      for (let j = 0; j < eccLen; j++) res[j] = res[j]! ^ gfMul(gen[j + 1]!, factor);
    }
  }
  return res;
}

// ─── Version capacity table (byte mode, ECC level L) ─────────────────────────
// [version] = { totalBytes, dataBytes, eccPerBlock, blocks }
// Values from ISO/IEC 18004 Annex B. Single-block versions only (V1–V10).

interface VersionSpec {
  version: number;
  totalBytes: number;
  dataBytes: number;
  ecc: number;
  blocks: number;
}

const VERSIONS_L: VersionSpec[] = [
  { version: 1, totalBytes: 26, dataBytes: 19, ecc: 7, blocks: 1 },
  { version: 2, totalBytes: 44, dataBytes: 34, ecc: 10, blocks: 1 },
  { version: 3, totalBytes: 70, dataBytes: 55, ecc: 15, blocks: 1 },
  { version: 4, totalBytes: 100, dataBytes: 80, ecc: 20, blocks: 1 },
  { version: 5, totalBytes: 134, dataBytes: 108, ecc: 26, blocks: 1 },
  { version: 6, totalBytes: 172, dataBytes: 136, ecc: 18, blocks: 2 },
  { version: 7, totalBytes: 196, dataBytes: 156, ecc: 20, blocks: 2 },
  { version: 8, totalBytes: 242, dataBytes: 194, ecc: 24, blocks: 2 },
  { version: 9, totalBytes: 292, dataBytes: 232, ecc: 30, blocks: 2 },
  { version: 10, totalBytes: 346, dataBytes: 274, ecc: 36, blocks: 2 },
];

const ALIGN_PATTERNS: Record<number, number[]> = {
  1: [],
  2: [6, 18],
  3: [6, 22],
  4: [6, 26],
  5: [6, 30],
  6: [6, 34],
  7: [6, 22, 38],
  8: [6, 24, 42],
  9: [6, 26, 46],
  10: [6, 28, 50],
};

// ─── Bit stream ──────────────────────────────────────────────────────────────

class BitStream {
  private readonly bits: number[] = [];
  push(value: number, count: number) {
    for (let i = count - 1; i >= 0; i--) this.bits.push((value >> i) & 1);
  }
  toBytes(): number[] {
    while (this.bits.length % 8 !== 0) this.bits.push(0);
    const out: number[] = [];
    for (let i = 0; i < this.bits.length; i += 8) {
      let b = 0;
      for (let j = 0; j < 8; j++) b = (b << 1) | this.bits[i + j]!;
      out.push(b);
    }
    return out;
  }
  get bitLength() {
    return this.bits.length;
  }
}

// ─── Encode data to bytes + ECC ──────────────────────────────────────────────

function pickVersion(dataLen: number): VersionSpec {
  for (const v of VERSIONS_L) {
    // Byte mode header for versions 1-9: 4 bits mode + 8 bits length = 12 bits.
    // Versions 10+: 4 bits mode + 16 bits length = 20 bits.
    const header = v.version < 10 ? 12 : 20;
    const terminator = 4;
    const capacityBits = v.dataBytes * 8;
    const needed = header + dataLen * 8 + terminator;
    if (capacityBits >= needed) return v;
  }
  throw new Error('QR payload too long for built-in version table (max V10, ECC L).');
}

function buildCodewords(text: string, spec: VersionSpec): number[] {
  const utf8 = new TextEncoder().encode(text);
  const bs = new BitStream();
  bs.push(0b0100, 4); // byte mode
  bs.push(utf8.length, spec.version < 10 ? 8 : 16);
  for (const byte of utf8) bs.push(byte, 8);
  // Terminator up to 4 zero bits (if room).
  const capacityBits = spec.dataBytes * 8;
  const termLen = Math.min(4, capacityBits - bs.bitLength);
  if (termLen > 0) bs.push(0, termLen);
  const bytes = bs.toBytes();
  // Pad to data capacity with alternating 0xEC / 0x11.
  const pad = [0xec, 0x11];
  let i = 0;
  while (bytes.length < spec.dataBytes) {
    bytes.push(pad[i % 2]!);
    i++;
  }
  // Split into blocks, compute ECC per block, then interleave.
  const perBlockData = Math.floor(spec.dataBytes / spec.blocks);
  const extraData = spec.dataBytes - perBlockData * spec.blocks;
  const dataBlocks: number[][] = [];
  const eccBlocks: number[][] = [];
  let cursor = 0;
  for (let b = 0; b < spec.blocks; b++) {
    const size = perBlockData + (b >= spec.blocks - extraData ? 1 : 0);
    const block = bytes.slice(cursor, cursor + size);
    cursor += size;
    dataBlocks.push(block);
    eccBlocks.push(rsEncode(block, spec.ecc));
  }
  const maxDataLen = Math.max(...dataBlocks.map((b) => b.length));
  const interleaved: number[] = [];
  for (let i2 = 0; i2 < maxDataLen; i2++) {
    for (const blk of dataBlocks) if (i2 < blk.length) interleaved.push(blk[i2]!);
  }
  for (let i2 = 0; i2 < spec.ecc; i2++) {
    for (const blk of eccBlocks) interleaved.push(blk[i2]!);
  }
  return interleaved;
}

// ─── Matrix construction ─────────────────────────────────────────────────────

function sizeOfVersion(v: number) {
  return v * 4 + 17;
}

function placeFinderAndSeparators(m: Matrix, reserved: boolean[][]) {
  const n = m.length;
  const drawFinder = (r0: number, c0: number) => {
    for (let r = -1; r <= 7; r++) {
      for (let c = -1; c <= 7; c++) {
        const rr = r0 + r;
        const cc = c0 + c;
        if (rr < 0 || cc < 0 || rr >= n || cc >= n) continue;
        reserved[rr]![cc] = true;
        const onOuter = (r >= 0 && r <= 6 && (c === 0 || c === 6)) || (c >= 0 && c <= 6 && (r === 0 || r === 6));
        const onInner = r >= 2 && r <= 4 && c >= 2 && c <= 4;
        m[rr]![cc] = onOuter || onInner;
      }
    }
  };
  drawFinder(0, 0);
  drawFinder(0, n - 7);
  drawFinder(n - 7, 0);
}

function placeTiming(m: Matrix, reserved: boolean[][]) {
  const n = m.length;
  for (let i = 8; i < n - 8; i++) {
    m[6]![i] = i % 2 === 0;
    m[i]![6] = i % 2 === 0;
    reserved[6]![i] = true;
    reserved[i]![6] = true;
  }
}

function placeAlignment(m: Matrix, reserved: boolean[][], version: number) {
  const centers = ALIGN_PATTERNS[version] ?? [];
  const n = m.length;
  for (const r of centers) {
    for (const c of centers) {
      // Skip if overlapping a finder.
      if ((r <= 8 && c <= 8) || (r <= 8 && c >= n - 9) || (r >= n - 9 && c <= 8)) continue;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          const onOuter = Math.abs(dr) === 2 || Math.abs(dc) === 2;
          const onCenter = dr === 0 && dc === 0;
          m[r + dr]![c + dc] = onOuter || onCenter;
          reserved[r + dr]![c + dc] = true;
        }
      }
    }
  }
}

function reserveFormat(reserved: boolean[][]) {
  const n = reserved.length;
  for (let i = 0; i <= 8; i++) {
    reserved[8]![i] = true;
    reserved[i]![8] = true;
  }
  for (let i = 0; i < 8; i++) {
    reserved[8]![n - 1 - i] = true;
    reserved[n - 1 - i]![8] = true;
  }
  reserved[n - 8]![8] = true; // dark module
}

function placeData(m: Matrix, reserved: boolean[][], bits: number[]) {
  const n = m.length;
  let bit = 0;
  for (let col = n - 1; col > 0; col -= 2) {
    if (col === 6) col--; // skip timing column
    for (let i = 0; i < n; i++) {
      const upward = ((col + 1) & 2) === 0 ? false : true;
      const row = upward ? n - 1 - i : i;
      for (const dc of [0, -1]) {
        const c = col + dc;
        if (!reserved[row]![c]) {
          m[row]![c] = bit < bits.length ? bits[bit] === 1 : false;
          bit++;
        }
      }
    }
  }
}

const MASKS: Array<(r: number, c: number) => boolean> = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

function applyMask(m: Matrix, reserved: boolean[][], maskFn: (r: number, c: number) => boolean) {
  const n = m.length;
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (reserved[r]![c]) continue;
      if (maskFn(r, c)) m[r]![c] = !m[r]![c];
    }
  }
}

function placeFormat(m: Matrix, maskIndex: number) {
  const n = m.length;
  // Format info: 2 bits ECC level (L = 01), 3 bits mask, 10 bits BCH ECC, XOR 0x5412.
  const levelBits = 0b01;
  const data = (levelBits << 3) | maskIndex;
  let rem = data;
  for (let i = 0; i < 10; i++) {
    if ((rem >> (9 - i)) & 1) {
      rem ^= 0b10100110111 << (9 - i);
    }
  }
  const format = (((data << 10) | rem) ^ 0b101010000010010) & 0x7fff;
  const getBit = (i: number) => ((format >> i) & 1) === 1;
  // Top-left.
  for (let i = 0; i <= 5; i++) m[8]![i] = getBit(i);
  m[8]![7] = getBit(6);
  m[8]![8] = getBit(7);
  m[7]![8] = getBit(8);
  for (let i = 9; i <= 14; i++) m[14 - i]![8] = getBit(i);
  // Bottom-left + top-right.
  for (let i = 0; i < 8; i++) m[n - 1 - i]![8] = getBit(i);
  for (let i = 8; i < 15; i++) m[8]![n - 15 + i] = getBit(i);
  m[n - 8]![8] = true; // dark module
}

function scoreMask(m: Matrix): number {
  const n = m.length;
  let score = 0;
  // Rule 1: runs of 5+ same-color in row/col.
  for (let r = 0; r < n; r++) {
    let run = 1;
    for (let c = 1; c < n; c++) {
      if (m[r]![c] === m[r]![c - 1]) run++;
      else {
        if (run >= 5) score += 3 + (run - 5);
        run = 1;
      }
    }
    if (run >= 5) score += 3 + (run - 5);
  }
  for (let c = 0; c < n; c++) {
    let run = 1;
    for (let r = 1; r < n; r++) {
      if (m[r]![c] === m[r - 1]![c]) run++;
      else {
        if (run >= 5) score += 3 + (run - 5);
        run = 1;
      }
    }
    if (run >= 5) score += 3 + (run - 5);
  }
  // Rule 2: 2x2 same color.
  for (let r = 0; r < n - 1; r++) {
    for (let c = 0; c < n - 1; c++) {
      const v = m[r]![c];
      if (v === m[r]![c + 1] && v === m[r + 1]![c] && v === m[r + 1]![c + 1]) score += 3;
    }
  }
  // Rule 4: proportion of dark modules.
  let dark = 0;
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (m[r]![c]) dark++;
  const ratio = (dark * 100) / (n * n);
  score += Math.floor(Math.abs(ratio - 50) / 5) * 10;
  return score;
}

// ─── Public ──────────────────────────────────────────────────────────────────

export function generateQrMatrix(text: string): Matrix {
  const spec = pickVersion(new TextEncoder().encode(text).length);
  const codewords = buildCodewords(text, spec);
  const bits: number[] = [];
  for (const b of codewords) for (let i = 7; i >= 0; i--) bits.push((b >> i) & 1);

  const n = sizeOfVersion(spec.version);
  const build = (): [Matrix, boolean[][]] => {
    const m: Matrix = Array.from({ length: n }, () => Array.from({ length: n }, () => false));
    const reserved: boolean[][] = Array.from({ length: n }, () => Array.from({ length: n }, () => false));
    placeFinderAndSeparators(m, reserved);
    placeTiming(m, reserved);
    placeAlignment(m, reserved, spec.version);
    reserveFormat(reserved);
    placeData(m, reserved, bits);
    return [m, reserved];
  };

  let best: { m: Matrix; score: number; mask: number } | null = null;
  for (let maskIdx = 0; maskIdx < 8; maskIdx++) {
    const [m, reserved] = build();
    applyMask(m, reserved, MASKS[maskIdx]!);
    placeFormat(m, maskIdx);
    const score = scoreMask(m);
    if (!best || score < best.score) best = { m, score, mask: maskIdx };
  }
  return best!.m;
}
